import React, { useState, useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronDown, Menu, X, BookOpen, BookMarked, GraduationCap, Heart, ShoppingBag, Layers, Database, Rocket, Palette, BarChart3, MessageSquare, UserPlus, Handshake } from 'lucide-react';
import { useUser } from '../../../context/UserContext';
import { keyframes } from '@emotion/react';

const NavContainer = styled.nav<{ scrolled: boolean; isMobileMenuOpen?: boolean }>`
  position: sticky;
  top: 0;
  z-index: 1000;
  width: 100%;
  background: ${props => (props.scrolled || props.isMobileMenuOpen)
    ? 'rgba(255, 255, 255, 0.95)'
    : 'transparent'};
  backdrop-filter: ${props => (props.scrolled || props.isMobileMenuOpen) ? 'blur(20px)' : 'none'};
  -webkit-backdrop-filter: ${props => (props.scrolled || props.isMobileMenuOpen) ? 'blur(20px)' : 'none'};
  border-bottom: 1px solid ${props => (props.scrolled || props.isMobileMenuOpen) ? 'rgba(15, 23, 42, 0.06)' : 'transparent'};
  padding: 0;
  transition: background 0.25s ease, border-color 0.25s ease, box-shadow 0.25s ease;
  box-shadow: ${props => (props.scrolled || props.isMobileMenuOpen) ? '0 4px 20px rgba(0, 0, 0, 0.03)' : 'none'};
`;

const NavContent = styled.div`
  max-width: var(--landing-content-max);
  width: 100%;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 70px;
  padding: 0 var(--landing-page-gutter);

  @media (max-width: 968px) {
    padding: 0 var(--landing-page-gutter);
    height: 72px;
  }
`;

const LogoSection = styled(Link)`
  display: flex;
  align-items: center;
  gap: 6px;
  text-decoration: none;
  color: inherit;
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.02);
  }
`;

const LogoImage = styled.img`
  height: 30px;
  width: auto;
  object-fit: contain;
`;

const LogoText = styled.span`
  font-family: var(--font-sans);
  font-size: 18px;
  font-weight: 700;
  color: #0b0f1a;
  letter-spacing: -0.02em;
`;

const NavLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex: 1;
  justify-content: center;
  margin: 0 48px;

  @media (max-width: 968px) {
    display: none;
  }
`;

const NavItem = styled.div`
  position: relative;
`;

const NavButton = styled.button<{ isActive?: boolean }>`
  font-family: var(--font-sans);
  font-size: 15px;
  font-weight: 500;
  color: ${props => props.isActive ? '#6366f1' : '#0b0f1a'};
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  border-radius: 10px;
  transition: all 0.15s ease;
  position: relative;

  &:hover {
    color:rgb(0, 0, 0);
    font-weight: 500;
    // background: rgba(99, 102, 241, 0.08);
  }

  svg {
    width: 16px;
    height: 16px;
    transition: transform 0.2s ease;
  }

  &[aria-expanded="true"] svg {
    transform: rotate(180deg);
  }
`;

const NavButtonLink = styled(Link, { shouldForwardProp: (prop) => prop !== 'isActive' }) <{ isActive?: boolean }>`
  font-family: var(--font-sans);
  font-size: 15px;
  font-weight: 500;
  color: ${props => props.isActive ? '#6366f1' : '#0b0f1a'};
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  border-radius: 10px;
  transition: all 0.15s ease;
  position: relative;
  text-decoration: none;

  &:hover {
    color:rgb(0, 0, 0);
    font-weight: 500;
    // background: rgba(99, 102, 241, 0.08);
  }

  svg {
    width: 16px;
    height: 16px;
    transition: transform 0.2s ease;
  }

  &[aria-expanded="true"] svg {
    transform: rotate(180deg);
  }
`;

const NavLink = styled.a`
  font-family: var(--font-sans);
  font-size: 15px;
  font-weight: 500;
  color: #0b0f1a;
  text-decoration: none;
  cursor: pointer;
  padding: 10px 16px;
  border-radius: 10px;
  transition: all 0.15s ease;

  &:hover {
    color:rgb(0, 0, 0);
    font-weight: 500;
    // background: rgba(99, 102, 241, 0.08);
  }
`;

const NavRouterLink = styled(Link)`
  font-family: var(--font-sans);
  font-size: 15px;
  font-weight: 500;
  color: #0b0f1a;
  text-decoration: none;
  cursor: pointer;
  padding: 10px 16px;
  border-radius: 10px;
  transition: all 0.15s ease;

  &:hover {
    color:rgb(0, 0, 0);
    font-weight: 500;
  }
`;

const glimmer = keyframes`
  0% {
    background-position: 0% center;
  }
  50% {
    background-position: 100% center;
  }
  100% {
    background-position: 0% center;
  }
`;

const LaunchDealLink = styled(NavLink)`
  background: linear-gradient(
    90deg,
    #6366f1,
    #a855f7,
    #ec4899,
    #a855f7,
    #6366f1
  );
  background-size: 200% auto;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: ${glimmer} 3s linear infinite;
  font-weight: 500;
`;

const WideTray = styled.div<{ isOpen: boolean; trayType: string }>`
  position: absolute;
  top: calc(100% + 12px);
  left: 50%;
  transform: translateX(-50%);
  width: 680px;
  background: white;
  border: 1px solid rgba(221, 214, 254, 0.3);
  border-radius: 16px;
  padding: 32px;
  pointer-events: ${props => props.isOpen ? 'auto' : 'none'};
  opacity: ${props => props.isOpen ? 1 : 0};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transform: ${props => props.isOpen
    ? 'translateX(-50%) translateY(0) scale(1)'
    : 'translateX(-50%) translateY(-8px) scale(0.98)'};
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 1000;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);

  &::before {
    content: '';
    position: absolute;
    top: -8px;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
    border-bottom: 8px solid white;
  }

  /* Bridge to prevent dropdown from closing when moving mouse from button to menu */
  &::after {
    content: '';
    position: absolute;
    top: -12px;
    left: 0;
    right: 0;
    height: 12px;
    background: transparent;
    pointer-events: ${props => props.isOpen ? 'auto' : 'none'};
  }

  @media (max-width: 968px) {
    display: none;
  }
`;

const TrayGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
`;

const TrayItem = styled(Link)`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 20px;
  border-radius: 12px;
  text-decoration: none;
  color: inherit;
  transition: all 0.15s ease;
  border: 1px solid transparent;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.8), rgba(221, 214, 254, 0.03));

  &:hover {
    background: linear-gradient(135deg, rgba(99, 102, 241, 0.06), rgba(139, 92, 246, 0.03));
    border-color: rgba(221, 214, 254, 0.4);
    transform: translateY(-1px);
  }
`;

const TrayPlaceholder = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 20px;
  border-radius: 12px;
  color: inherit;
  border: 1px solid transparent;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.8), rgba(221, 214, 254, 0.03));
  cursor: default;
`;

const ComingSoonTag = styled.span`
  display: inline-block;
  margin-left: 8px;
  padding: 2px 6px;
  border: 1px solid #dedfe3;
  border-radius: 999px;
  color: #777b84;
  font-size: 9px;
  font-weight: 500;
  letter-spacing: 0.04em;
  line-height: 1.3;
  text-transform: uppercase;
  vertical-align: middle;
`;

const TrayIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: linear-gradient(135deg, rgba(138, 43, 226, 0.1), rgba(59, 130, 246, 0.1));
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #6366f1;

  svg {
    width: 24px;
    height: 24px;
  }
`;

const TrayContent = styled.div`
  flex: 1;
`;

const TrayTitle = styled.div`
  font-family: var(--font-sans);
  font-size: 16px;
  font-weight: 400;
  color: #0b0f1a;
  margin-bottom: 6px;
  line-height: 1.3;
`;

const TrayDescription = styled.div`
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 14px;
  color: #6b7280;
  line-height: 1.5;
`;

const ActionButton = styled.button`
  font-family: var(--font-sans);
  font-size: 14px;
  font-weight: 500;
  padding: 8px 20px;
  border-radius: var(--smush-cta-radius);
  border: none;
  cursor: pointer;
  height: 36px;
  transition: background-color 0.2s ease;
  background: var(--smush-cta-primary);
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: var(--smush-cta-primary-hover);
  }

  @media (max-width: 968px) {
    display: none;
  }
`;

const LoginButton = styled.button`
  font-family: var(--font-sans);
  font-size: 14px;
  font-weight: 500;
  padding: 8px 20px;
  border-radius: var(--smush-cta-radius);
  border: 1px solid var(--smush-cta-secondary-border);
  cursor: pointer;
  height: 36px;
  transition: background-color 0.2s ease, border-color 0.2s ease;
  background: var(--smush-cta-secondary);
  color: var(--smush-cta-primary);
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: var(--smush-cta-secondary-hover);
    border-color: rgba(45, 54, 71, 0.34);
  }

  @media (max-width: 968px) {
    display: none;
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  color: #0b0f1a;
  border-radius: 8px;
  transition: background 0.15s ease;

  &:hover {
    background: rgba(99, 102, 241, 0.08);
  }

  @media (max-width: 968px) {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  svg {
    width: 24px;
    height: 24px;
  }
`;

const MobileMenu = styled.div<{ isOpen: boolean }>`
  display: none;
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border-top: ${props => props.isOpen ? '1px solid rgba(221, 214, 254, 0.2)' : 'none'};
  padding: ${props => props.isOpen ? '24px 32px' : '0 32px'};
  max-height: ${props => props.isOpen ? '90vh' : '0'};
  overflow-y: auto;
  overflow-x: hidden;
  transition: max-height 0.3s cubic-bezier(0.4, 0, 0.2, 1), padding 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s, visibility 0.3s;
  box-shadow: ${props => props.isOpen ? '0 4px 16px rgba(0, 0, 0, 0.06)' : 'none'};
  opacity: ${props => props.isOpen ? '1' : '0'};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};

  @media (max-width: 968px) {
    display: block;
  }
`;

const MobileNavLink = styled.a`
  display: block;
  font-family: var(--font-sans);
  font-size: 16px;
  font-weight: 600;
  color: #0b0f1a;
  text-decoration: none;
  padding: 16px 0;
  border-bottom: 1px solid rgba(221, 214, 254, 0.2);
  cursor: pointer;
  transition: color 0.15s ease;

  &:hover {
    color: #6366f1;
  }
`;

const MobileNavRouterLink = styled(Link)`
  display: block;
  font-family: var(--font-sans);
  font-size: 16px;
  font-weight: 600;
  color: #0b0f1a;
  text-decoration: none;
  padding: 16px 0;
  border-bottom: 1px solid rgba(221, 214, 254, 0.2);
  cursor: pointer;
  transition: color 0.15s ease;

  &:hover {
    color: #6366f1;
  }
`;

const MobileLaunchDealLink = styled(MobileNavLink)`
  background: linear-gradient(
    90deg,
    #6366f1,
    #a855f7,
    #ec4899,
    #a855f7,
    #6366f1
  );
  background-size: 200% auto;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: ${glimmer} 3s linear infinite;
  font-weight: 500;
`;

const MobileSection = styled.div`
  padding: 16px 0;
  border-bottom: 1px solid rgba(221, 214, 254, 0.2);
`;

const MobileSectionTitle = styled.div`
  font-family: var(--font-sans);
  font-size: 16px;
  font-weight: 700;
  color: #0b0f1a;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const MobileSectionItem = styled(Link)`
  display: flex;
  align-items: center;
  gap: 12px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #6b7280;
  text-decoration: none;
  padding: 12px 0 12px 32px;
  cursor: pointer;
  transition: color 0.15s ease;

  &:hover {
    color: #6366f1;
  }

  svg {
    width: 18px;
    height: 18px;
    color: #6366f1;
  }
`;

const MobileSectionPlaceholder = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 0 12px 32px;
  color: #6b7280;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 14px;
  font-weight: 500;

  svg {
    width: 18px;
    height: 18px;
    color: #9ca3af;
  }
`;

const MobileLoginButton = styled.button`
  width: 100%;
  margin-top: 24px;
  font-family: var(--font-sans);
  font-size: 15px;
  font-weight: 500;
  padding: 12px 20px;
  border-radius: var(--smush-cta-radius);
  border: 1px solid var(--smush-cta-secondary-border);
  cursor: pointer;
  transition: background-color 0.2s ease, border-color 0.2s ease;
  background: var(--smush-cta-secondary);
  color: var(--smush-cta-primary);

  &:hover {
    background: var(--smush-cta-secondary-hover);
    border-color: rgba(45, 54, 71, 0.34);
  }
`;

const MobileActionButton = styled.button`
  width: 100%;
  margin-top: 12px;
  font-family: var(--font-sans);
  font-size: 15px;
  font-weight: 500;
  padding: 12px 20px;
  border-radius: var(--smush-cta-radius);
  border: none;
  cursor: pointer;
  transition: background-color 0.2s ease;
  background: var(--smush-cta-primary);
  color: #ffffff;

  &:hover {
    background: var(--smush-cta-primary-hover);
  }
`;

interface NavigationProps { }

const Navigation: React.FC<NavigationProps> = () => {
  const [activeTray, setActiveTray] = useState<string | null>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const featuresRef = useRef<HTMLDivElement>(null);
  const useCasesRef = useRef<HTMLDivElement>(null);
  const resourceRef = useRef<HTMLDivElement>(null);
  const { user } = useUser();
  const navigate = useNavigate();

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      if (activeTray === 'features' && featuresRef.current && !featuresRef.current.contains(target)) {
        setActiveTray(null);
      }
      if (activeTray === 'use-cases' && useCasesRef.current && !useCasesRef.current.contains(target)) {
        setActiveTray(null);
      }
      if (activeTray === 'resource' && resourceRef.current && !resourceRef.current.contains(target)) {
        setActiveTray(null);
      }
    };

    if (activeTray) {
      // Use setTimeout to avoid immediate closure
      setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 0);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [activeTray]);

  const handleActionClick = () => {
    if (user) {
      navigate('/dashboard');
    } else {
      navigate('/get-started');
    }
    setIsMobileMenuOpen(false);
  };

  const handleLoginClick = () => {
    navigate('/login');
    setIsMobileMenuOpen(false);
  };

  const toggleTray = (trayName: string) => {
    // Immediately set the new tray, closing others
    setActiveTray(prev => prev === trayName ? null : trayName);
  };

  return (
    <NavContainer scrolled={scrolled} isMobileMenuOpen={isMobileMenuOpen}>
      <NavContent>
        <LogoSection to="/">
          <LogoImage src="/ModerLogo.png" alt="SmushChat Logo" onError={(e) => {
            (e.target as HTMLImageElement).src = '/favicon.ico';
          }} />
          <LogoText>SmushLabs</LogoText>
        </LogoSection>

        <NavLinks>
          <NavItem
            ref={featuresRef}
            onMouseEnter={() => setActiveTray('features')}
            onMouseLeave={() => setActiveTray(null)}
          >
            <NavButtonLink
              to="/features"
              onClick={() => setActiveTray(null)} // Close tray on click since we are navigating
              aria-expanded={activeTray === 'features'}
              isActive={activeTray === 'features'}
            >
              Features
              <ChevronDown />
            </NavButtonLink>
            <WideTray isOpen={activeTray === 'features'} trayType="features">
              <TrayGrid>
                <TrayItem to="/features#multi-brand" onClick={() => setActiveTray(null)}>
                  <TrayIcon>
                    <Layers />
                  </TrayIcon>
                  <TrayContent>
                    <TrayTitle>Multi-brand Support</TrayTitle>
                    <TrayDescription>Manage multiple brands from one dashboard</TrayDescription>
                  </TrayContent>
                </TrayItem>
                <TrayItem to="/features#files" onClick={() => setActiveTray(null)}>
                  <TrayIcon>
                    <Database />
                  </TrayIcon>
                  <TrayContent>
                    <TrayTitle>Custom Knowledge Base</TrayTitle>
                    <TrayDescription>Train your AI with your own data</TrayDescription>
                  </TrayContent>
                </TrayItem>
                <TrayItem to="/features#chat-widget" onClick={() => setActiveTray(null)}>
                  <TrayIcon>
                    <Rocket />
                  </TrayIcon>
                  <TrayContent>
                    <TrayTitle>1 Click Deploy</TrayTitle>
                    <TrayDescription>Deploy your chatbot instantly</TrayDescription>
                  </TrayContent>
                </TrayItem>
                <TrayItem to="/features#customization" onClick={() => setActiveTray(null)}>
                  <TrayIcon>
                    <Palette />
                  </TrayIcon>
                  <TrayContent>
                    <TrayTitle>Customisations</TrayTitle>
                    <TrayDescription>Personalize look and feel to match your brand</TrayDescription>
                  </TrayContent>
                </TrayItem>
                <TrayItem to="/features#analytics" onClick={() => setActiveTray(null)}>
                  <TrayIcon>
                    <BarChart3 />
                  </TrayIcon>
                  <TrayContent>
                    <TrayTitle>Analytics</TrayTitle>
                    <TrayDescription>Track performance and user engagement</TrayDescription>
                  </TrayContent>
                </TrayItem>
                <TrayItem to="/features#transcripts" onClick={() => setActiveTray(null)}>
                  <TrayIcon>
                    <MessageSquare />
                  </TrayIcon>
                  <TrayContent>
                    <TrayTitle>Chat Review</TrayTitle>
                    <TrayDescription>Review and improve chat conversations</TrayDescription>
                  </TrayContent>
                </TrayItem>
                <TrayItem to="/features#lead-gen" onClick={() => setActiveTray(null)}>
                  <TrayIcon>
                    <UserPlus />
                  </TrayIcon>
                  <TrayContent>
                    <TrayTitle>Lead Generation</TrayTitle>
                    <TrayDescription>Capture and qualify leads automatically</TrayDescription>
                  </TrayContent>
                </TrayItem>
              </TrayGrid>
            </WideTray>
          </NavItem>

          <NavItem
            ref={useCasesRef}
            onMouseEnter={() => setActiveTray('use-cases')}
            onMouseLeave={() => setActiveTray(null)}
          >
            <NavButton
              onClick={() => toggleTray('use-cases')}
              aria-expanded={activeTray === 'use-cases'}
              isActive={activeTray === 'use-cases'}
            >
              Use Cases
              <ChevronDown />
            </NavButton>
            <WideTray isOpen={activeTray === 'use-cases'} trayType="use-cases">
              <TrayGrid>
                <TrayItem to="/education" onClick={() => setIsMobileMenuOpen(false)}>
                  <TrayIcon>
                    <GraduationCap />
                  </TrayIcon>
                  <TrayContent>
                    <TrayTitle>Education</TrayTitle>
                    <TrayDescription>AI solutions for schools and universities</TrayDescription>
                  </TrayContent>
                </TrayItem>
                <TrayPlaceholder aria-disabled="true">
                  <TrayIcon><Heart /></TrayIcon>
                  <TrayContent>
                    <TrayTitle>Health &amp; Wellness <ComingSoonTag>Coming soon</ComingSoonTag></TrayTitle>
                    <TrayDescription>Patient support and appointment guidance</TrayDescription>
                  </TrayContent>
                </TrayPlaceholder>
                <TrayPlaceholder aria-disabled="true">
                  <TrayIcon><ShoppingBag /></TrayIcon>
                  <TrayContent>
                    <TrayTitle>Ecommerce <ComingSoonTag>Coming soon</ComingSoonTag></TrayTitle>
                    <TrayDescription>Sales support and product recommendations</TrayDescription>
                  </TrayContent>
                </TrayPlaceholder>
              </TrayGrid>
            </WideTray>
          </NavItem>

          <NavRouterLink to="/pricing">Pricing</NavRouterLink>
          {/* <NavRouterLink to="/blog">Blog</NavRouterLink> */}

          <LaunchDealLink onClick={() => navigate('/launch-deal')}>
            Launch Deal
          </LaunchDealLink>

          <NavItem
            ref={resourceRef}
            onMouseEnter={() => setActiveTray('resource')}
            onMouseLeave={() => setActiveTray(null)}
          >
            <NavButton
              onClick={() => toggleTray('resource')}
              aria-expanded={activeTray === 'resource'}
              isActive={activeTray === 'resource'}
            >
              Resources
              <ChevronDown />
            </NavButton>
            <WideTray isOpen={activeTray === 'resource'} trayType="resource">
              <TrayGrid>
                <TrayItem to="/guides" onClick={() => setActiveTray(null)}>
                  <TrayIcon>
                    <BookMarked />
                  </TrayIcon>
                  <TrayContent>
                    <TrayTitle>Guides</TrayTitle>
                    <TrayDescription>Product documentation and how-to articles</TrayDescription>
                  </TrayContent>
                </TrayItem>
                <TrayItem to="/research" onClick={() => setActiveTray(null)}>
                  <TrayIcon><BookOpen /></TrayIcon>
                  <TrayContent><TrayTitle>Research</TrayTitle><TrayDescription>How SmushChat works and the ideas behind the product</TrayDescription></TrayContent>
                </TrayItem>
                <TrayItem to="/blog" onClick={() => setActiveTray(null)}>
                  <TrayIcon><BookOpen /></TrayIcon>
                  <TrayContent><TrayTitle>Blog</TrayTitle><TrayDescription>Insights, product thinking, and customer stories</TrayDescription></TrayContent>
                </TrayItem>
                <TrayItem to="/affiliates" onClick={() => setActiveTray(null)}>
                  <TrayIcon><Handshake /></TrayIcon>
                  <TrayContent><TrayTitle>Affiliate Program</TrayTitle><TrayDescription>Earn up to 35% recurring revenue introducing clients</TrayDescription></TrayContent>
                </TrayItem>
                <TrayPlaceholder aria-disabled="true">
                  <TrayIcon><Database /></TrayIcon>
                  <TrayContent><TrayTitle>API Docs <ComingSoonTag>Coming soon</ComingSoonTag></TrayTitle><TrayDescription>Developer reference and integration guides</TrayDescription></TrayContent>
                </TrayPlaceholder>
                <TrayPlaceholder aria-disabled="true">
                  <TrayIcon><Rocket /></TrayIcon>
                  <TrayContent><TrayTitle>Roadmap <ComingSoonTag>Coming soon</ComingSoonTag></TrayTitle><TrayDescription>What we are building next</TrayDescription></TrayContent>
                </TrayPlaceholder>
              </TrayGrid>
            </WideTray>
          </NavItem>
        </NavLinks>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {user ? (
            <ActionButton onClick={handleActionClick}>
              Dashboard
            </ActionButton>
          ) : (
            <>
              <LoginButton onClick={handleLoginClick}>
                Log In
              </LoginButton>
              <ActionButton onClick={handleActionClick}>
                Get Started
              </ActionButton>
            </>
          )}
          <MobileMenuButton onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
            {isMobileMenuOpen ? <X /> : <Menu />}
          </MobileMenuButton>
        </div>
      </NavContent>

      <MobileMenu isOpen={isMobileMenuOpen}>
        <MobileSection>
          <MobileSectionTitle>Features</MobileSectionTitle>
          <MobileSectionItem to="/features#multi-brand" onClick={() => setIsMobileMenuOpen(false)}>
            <Layers />
            Multi-brand Support
          </MobileSectionItem>
          <MobileSectionItem to="/features#files" onClick={() => setIsMobileMenuOpen(false)}>
            <Database />
            Custom Knowledge Base
          </MobileSectionItem>
          <MobileSectionItem to="/features#chat-widget" onClick={() => setIsMobileMenuOpen(false)}>
            <Rocket />
            1 Click Deploy
          </MobileSectionItem>
          <MobileSectionItem to="/features#customization" onClick={() => setIsMobileMenuOpen(false)}>
            <Palette />
            Customisations
          </MobileSectionItem>
          <MobileSectionItem to="/features#analytics" onClick={() => setIsMobileMenuOpen(false)}>
            <BarChart3 />
            Analytics
          </MobileSectionItem>
          <MobileSectionItem to="/features#transcripts" onClick={() => setIsMobileMenuOpen(false)}>
            <MessageSquare />
            Chat Review
          </MobileSectionItem>
          <MobileSectionItem to="/features#lead-gen" onClick={() => setIsMobileMenuOpen(false)}>
            <UserPlus />
            Lead Generation
          </MobileSectionItem>
        </MobileSection>

        <MobileSection>
          <MobileSectionTitle>Use Cases</MobileSectionTitle>
          <MobileSectionItem to="/education" onClick={() => setIsMobileMenuOpen(false)}>
            <GraduationCap />
            Education
          </MobileSectionItem>
          <MobileSectionPlaceholder aria-disabled="true"><Heart />Health &amp; Wellness <ComingSoonTag>Coming soon</ComingSoonTag></MobileSectionPlaceholder>
          <MobileSectionPlaceholder aria-disabled="true"><ShoppingBag />Ecommerce <ComingSoonTag>Coming soon</ComingSoonTag></MobileSectionPlaceholder>
        </MobileSection>

        <MobileNavRouterLink to="/pricing" onClick={() => setIsMobileMenuOpen(false)}>Pricing</MobileNavRouterLink>
        {/* <MobileNavRouterLink to="/blog" onClick={() => setIsMobileMenuOpen(false)}>Blog</MobileNavRouterLink> */}
        <MobileLaunchDealLink onClick={() => { navigate('/launch-deal'); setIsMobileMenuOpen(false); }}>
          Launch Deal
        </MobileLaunchDealLink>

        <MobileSection>
          <MobileSectionTitle>Resources</MobileSectionTitle>
          <MobileSectionItem to="/guides" onClick={() => setIsMobileMenuOpen(false)}>
            <BookMarked />
            Guides
          </MobileSectionItem>
          <MobileSectionItem to="/research" onClick={() => setIsMobileMenuOpen(false)}><BookOpen />Research</MobileSectionItem>
          <MobileSectionItem to="/blog" onClick={() => setIsMobileMenuOpen(false)}><BookOpen />Blog</MobileSectionItem>
          <MobileSectionItem to="/affiliates" onClick={() => setIsMobileMenuOpen(false)}><Handshake />Affiliate Program</MobileSectionItem>
          <MobileSectionPlaceholder aria-disabled="true"><Database />API Docs <ComingSoonTag>Coming soon</ComingSoonTag></MobileSectionPlaceholder>
          <MobileSectionPlaceholder aria-disabled="true"><Rocket />Roadmap <ComingSoonTag>Coming soon</ComingSoonTag></MobileSectionPlaceholder>
        </MobileSection>

        {user ? (
          <MobileActionButton onClick={handleActionClick}>
            Dashboard
          </MobileActionButton>
        ) : (
          <>
            <MobileLoginButton onClick={handleLoginClick}>
              Log In
            </MobileLoginButton>
            <MobileActionButton onClick={handleActionClick}>
              Get Started
            </MobileActionButton>
          </>
        )}
      </MobileMenu>
    </NavContainer>
  );
};

export default Navigation;
